import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CancelFedExPickup,
  CheckFedExPickupAvailability,
  GetFedExPickupPrefill,
  ScheduleFedExPickup,
  type FedExPickup,
  type FedExPickupPrefill,
  type PickupAvailabilityPayload,
  type PickupAvailabilityResponse,
  type SchedulePickupPayload,
} from '@/apis/fedexPickup';
import { unwrapEnvelopePayload } from '@/apis/envelope';
import { useToken } from '@/common/helper';
import { dispatchFedExPickupChanged } from '@/common/zoomlogiUiEvents';

const prefillKey = (shipmentId?: string) =>
  ['fedex-pickup-prefill', shipmentId] as const;

const requireToken = (checkTokenExpiry: () => string): string => {
  const token = checkTokenExpiry();
  if (!token) throw new Error('No token available');
  return token;
};

/**
 * Invalidation shared by schedule and cancel. Runs on success and error alike: a
 * 409/504 may still have changed state, so the prefill must be refetched.
 */
const useRefreshAfterPickupChange = (shipmentId: string | undefined) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: prefillKey(shipmentId) });
    queryClient.invalidateQueries({ queryKey: ['shipment-report'] });
    queryClient.invalidateQueries({ queryKey: ['shipments'] });
    if (shipmentId) dispatchFedExPickupChanged(shipmentId);
  };
};

/** Prefill for one shipment. `enabled=false` (gate off / non-FedEx) makes no call. */
export function usePickupPrefill(
  shipmentId: string | undefined,
  enabled = true
) {
  const { token, checkTokenExpiry } = useToken();
  const query = useQuery({
    queryKey: prefillKey(shipmentId),
    queryFn: async () => {
      const res = await GetFedExPickupPrefill(
        requireToken(checkTokenExpiry),
        shipmentId as string
      );
      const prefill = unwrapEnvelopePayload<FedExPickupPrefill>(res);
      if (!prefill) throw new Error('Unexpected response from FedEx pickup');
      return prefill;
    },
    enabled: enabled && !!shipmentId && !!token,
    staleTime: 60 * 1000,
    retry: false,
  });
  return { prefill: query.data ?? null, isPrefillLoading: query.isLoading };
}

export function useSchedulePickup(shipmentId: string | undefined) {
  const { checkTokenExpiry } = useToken();
  const mutation = useMutation({
    mutationFn: async (payload: SchedulePickupPayload) => {
      const res = await ScheduleFedExPickup(
        requireToken(checkTokenExpiry),
        payload
      );
      const pickup = unwrapEnvelopePayload<FedExPickup>(res);
      if (!pickup) throw new Error('Unexpected response from FedEx pickup');
      return pickup;
    },
    onSettled: useRefreshAfterPickupChange(shipmentId),
  });
  return { schedule: mutation.mutateAsync, isScheduling: mutation.isPending };
}

export function useCancelPickup(shipmentId: string | undefined) {
  const { checkTokenExpiry } = useToken();
  const mutation = useMutation({
    mutationFn: async (pickupId: string) => {
      const res = await CancelFedExPickup(
        requireToken(checkTokenExpiry),
        pickupId
      );
      const pickup = unwrapEnvelopePayload<FedExPickup>(res);
      if (!pickup) throw new Error('Unexpected response from FedEx pickup');
      return pickup;
    },
    onSettled: useRefreshAfterPickupChange(shipmentId),
  });
  return { cancel: mutation.mutateAsync, isCancelling: mutation.isPending };
}

/** Availability for the (already debounced) request; null disables the query. */
export function useFedExPickupAvailability(
  payload: PickupAvailabilityPayload | null
) {
  const { checkTokenExpiry } = useToken();
  return useQuery({
    queryKey: ['fedex-pickup-availability', payload],
    queryFn: async () => {
      const res = await CheckFedExPickupAvailability(
        requireToken(checkTokenExpiry),
        payload as PickupAvailabilityPayload
      );
      // A valid "nothing available" answer is `{ options: [] }`; a missing payload is a
      // contract failure and must surface as an error, not as "no options".
      const availability =
        unwrapEnvelopePayload<PickupAvailabilityResponse>(res);
      if (!availability)
        throw new Error('Unexpected response from FedEx pickup');
      return availability;
    },
    enabled: !!payload,
    staleTime: 30 * 1000,
    retry: false,
  });
}
